import { Box, Stack, Typography, useTheme } from "@mui/material";

import { PieChart, Pie, Legend, ResponsiveContainer } from "recharts";

import { useTranslation } from "react-i18next";

import TitleCase from "components/formatting/TitleCase";
import { defaultSettings, PieChartSettingsStateType } from "./PieChartSettings";
import { renderCustomizedLabel } from "./PieChart";

const wrapperStyle = {
  width: "max-content",
  maxWidth: "300px",
};

interface PieChartGraphPreviewProps {
  /**
   * State object represented by series of key value pairs,
   * where the key is a string which represents the title of the
   * updatable settings option.
   */
  settingsState: PieChartSettingsStateType | {};
}

export default function PieChartPreview(props: PieChartGraphPreviewProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const currentState = {...defaultSettings, ...props.settingsState};
  const chartColors = theme.colors.namedColors.chartColorArray;
  const dummyData = [
    { value: 450, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 1 })}`, fill: chartColors[0] },
    { value: 125, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 2 })}`, fill: chartColors[1] },
    { value: 75, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 3 })}`, fill: chartColors[2] },
    { value: 50, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 4 })}`, fill: chartColors[3] },
    { value: 50, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 5 })}`, fill: chartColors[4] },
    { value: 50, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 6 })}`, fill: chartColors[5] },
    { value: 50, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 7 })}`, fill: chartColors[6] },
    { value: 50, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 8 })}`, fill: chartColors[7] },
    { value: 50, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 9 })}`, fill: chartColors[8] },
    { value: 50, name: `${i18n.t("value")} ${i18n.t("intlNumber", { val: 10 })}`, fill: chartColors[9] },
  ];

  const allPieDataSum = dummyData.reduce((acc, curr) => acc + curr.value, 0);
  let dummyDataSorted = dummyData.sort((a: any, b: any) => b.value - a.value);

  // Keeps only the slices based off the max amount to display
  let maxSlicesPieData = dummyDataSorted.slice(0, currentState.maximumSlices);

  // Handles when user selects to view slices that are only above a certain percentage
  if (currentState.minimumPortion > 0) {
    maxSlicesPieData = maxSlicesPieData.filter(slice => {
      const sliceAsPortion = Math.round((slice.value / allPieDataSum) * 100);
      return sliceAsPortion >= currentState.minimumPortion;
    });
  }

  const maxSlicesPieDataSum = maxSlicesPieData.reduce((acc, curr) => acc + curr.value, 0);

  // If the pie data, after filtering for max slices & min value to display, doesn't equal 1000,
  // add "Other" group object w/ its value as the difference between total sum & current slices.
  if (maxSlicesPieDataSum < allPieDataSum) {
    maxSlicesPieData = [
      ...maxSlicesPieData,
      { value: allPieDataSum - maxSlicesPieDataSum, name: i18n.t("other"), fill: chartColors[dummyData.length] }
    ];
  }

  return (
    <Stack sx={{
      height: "100%"
    }}>
      {currentState.widgetTitle !== 'none' &&
        <Typography
          sx={{ fontSize: "1.2rem", textAlign: `${currentState.widgetTitleAlignment}` }}
          variant="subtitle2"
        >
          <TitleCase text={currentState.widgetTitle} />
        </Typography>}
      <Box
        sx={{
          alignSelf: "center",
          height: "300px",
          overflow: "hidden",
          width: "400px"
        }}>
        <ResponsiveContainer height="100%" width="100%">
          <PieChart>
            <Pie
              data={maxSlicesPieData}
              dataKey="value"
              endAngle={-270}
              fill={theme.palette.primary.main}
              innerRadius={`${currentState.innerRadius}%`}
              label={currentState.showLabels
                ? item => renderCustomizedLabel(item, currentState.labelType)
                : false}
              labelLine={false}
              startAngle={90} />
            {currentState.showLegend &&
              <Legend
                align={currentState.legendPosition.align}
                layout={currentState.legendDirection}
                verticalAlign={currentState.legendPosition.verticalAlign}
                wrapperStyle={currentState.legendPosition.verticalAlign === "middle" ? wrapperStyle : {}} />}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Stack>
  );
}